import { EducationHistoryEntry, InstitutionType } from '../types/user';

/**
 * "Most recent school attended" derivation (PTEC-517).
 *
 * The profile stores the CANONICAL education history list; the Lead-Share
 * transmittable field "most recent school attended" is derived from it and
 * never stored. Rule:
 *   - an entry without an endYear is ongoing → most recent;
 *   - otherwise the highest endYear wins;
 *   - ties go to the later entry (the list is user-curated, additions append).
 *
 * The lead delivery adapter (PTEC-521) must apply this SAME rule at delivery
 * time — see the derivation note in leadShareEnrollment.js FIELD_CATALOGUE.
 */
export const deriveMostRecentSchool = (history?: EducationHistoryEntry[] | null): EducationHistoryEntry | undefined => {
	let best: EducationHistoryEntry | undefined;
	let bestRank = -Infinity;
	for (const entry of history ?? []) {
		if (!entry?.institution?.trim()) {
			continue;
		}
		const rank = entry.endYear ?? Number.MAX_SAFE_INTEGER;
		if (rank >= bestRank) {
			bestRank = rank;
			best = entry;
		}
	}
	return best;
};

/** History ordered for display: most recent first (same ranking as derivation). */
export const sortMostRecentFirst = (history: EducationHistoryEntry[]): EducationHistoryEntry[] =>
	[...history]
		.map((entry, index) => ({ entry, index }))
		.sort((a, b) => {
			const rankA = a.entry.endYear ?? Number.MAX_SAFE_INTEGER;
			const rankB = b.entry.endYear ?? Number.MAX_SAFE_INTEGER;
			// Later additions win ties, mirroring deriveMostRecentSchool.
			return rankB - rankA || b.index - a.index;
		})
		.map(({ entry }) => entry);

/**
 * The single ongoing entry, if any (PTEC-517 rule: at most ONE school may be
 * ongoing — you can only attend one "current" school; everything else needs a
 * finish year). The editor enforces this on add.
 */
export const findOngoingEntry = (history?: EducationHistoryEntry[] | null): EducationHistoryEntry | undefined =>
	(history ?? []).find((entry) => Boolean(entry?.institution?.trim()) && entry.endYear == null);

/** Display form for an entry: "Institution · 2024", or just the name while ongoing. */
export const formatEducationEntry = (entry: EducationHistoryEntry): string =>
	entry.endYear ? `${entry.institution} · ${entry.endYear}` : entry.institution;

/**
 * Jurisdictions with distinct schooling vocabularies. Stored data is always
 * the canonical InstitutionType; only LABELS change per jurisdiction.
 */
export type EducationJurisdiction = 'UK' | 'US' | 'INTL';

const INSTITUTION_TYPE_LABELS: Record<EducationJurisdiction, Record<InstitutionType, string>> = {
	UK: {
		[InstitutionType.Primary]: 'Primary school',
		[InstitutionType.LowerSecondary]: 'Secondary school',
		[InstitutionType.UpperSecondary]: 'Sixth form / college',
		[InstitutionType.College]: 'College',
		[InstitutionType.University]: 'University',
		[InstitutionType.Vocational]: 'Apprenticeship / vocational',
		[InstitutionType.Other]: 'Other'
	},
	US: {
		[InstitutionType.Primary]: 'Elementary school',
		[InstitutionType.LowerSecondary]: 'Middle school',
		[InstitutionType.UpperSecondary]: 'High school',
		[InstitutionType.College]: 'College',
		[InstitutionType.University]: 'University',
		[InstitutionType.Vocational]: 'Trade / vocational school',
		[InstitutionType.Other]: 'Other'
	},
	INTL: {
		[InstitutionType.Primary]: 'Primary school',
		[InstitutionType.LowerSecondary]: 'Lower secondary school',
		[InstitutionType.UpperSecondary]: 'Upper secondary school',
		[InstitutionType.College]: 'College',
		[InstitutionType.University]: 'University',
		[InstitutionType.Vocational]: 'Vocational training',
		[InstitutionType.Other]: 'Other'
	}
};

export const institutionTypeLabel = (type: InstitutionType, jurisdiction: EducationJurisdiction): string =>
	INSTITUTION_TYPE_LABELS[jurisdiction][type];

/**
 * The noun a country uses for its first-level subdivisions, for UI copy only.
 *
 * The data layer deliberately calls these `subdivisions` and keys scopes as
 * `<country>#<subdivision>#<type>` so further countries need no reshaping. UI
 * copy has to match that, otherwise the labels are wrong for most of the world
 * the moment a second country is ingested.
 *
 * The US is intentionally absent, so it takes the neutral default: its
 * subdivision list is the 50 states PLUS the District of Columbia, which is a
 * state-equivalent in the source data and not a state. Calling that total
 * "states" reads as a data error to anyone who knows there are 50, and it gets
 * less accurate still if the territories are ever ingested.
 */
const SUBDIVISION_NOUNS: Record<string, { singular: string; plural: string }> = {
	GB: { singular: 'Nation', plural: 'Nations' },
	CA: { singular: 'Province', plural: 'Provinces' }
};

const DEFAULT_SUBDIVISION_NOUN = { singular: 'Jurisdiction', plural: 'Jurisdictions' };

/** Capitalised singular/plural subdivision noun for a country's ISO code. */
export const subdivisionNoun = (isoCountryCode?: string): { singular: string; plural: string } =>
	SUBDIVISION_NOUNS[isoCountryCode?.trim().toUpperCase() ?? ''] ?? DEFAULT_SUBDIVISION_NOUN;

/** The canonical stages in display order for the type pills. */
export const INSTITUTION_TYPES: InstitutionType[] = [
	InstitutionType.Primary,
	InstitutionType.LowerSecondary,
	InstitutionType.UpperSecondary,
	InstitutionType.College,
	InstitutionType.University,
	InstitutionType.Vocational,
	InstitutionType.Other
];

/**
 * Resolve the schooling vocabulary from what the profile already knows —
 * ISO country code (preferred) or citizenship name. Never asks for new data.
 */
export const resolveEducationJurisdiction = ({
	isoCountryCode,
	citizenship
}: {
	isoCountryCode?: string;
	citizenship?: string;
}): EducationJurisdiction => {
	const iso = isoCountryCode?.trim().toUpperCase();
	if (iso === 'GB' || iso === 'UK') {
		return 'UK';
	}
	if (iso === 'US') {
		return 'US';
	}
	const name = citizenship?.trim().toLowerCase() ?? '';
	if (name.includes('united kingdom') || name.includes('britain')) {
		return 'UK';
	}
	if (name.includes('united states') || name.includes('america')) {
		return 'US';
	}
	return 'INTL';
};
